import { AxiosResponse } from 'axios';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { BluViuNutritionParams } from 'views/bluviu-nutrition/types/bluviu-nutrition-params.type';
import { cloneScenario, postScenario, deleteScenario as removeScenario } from '../api/bluviu-nutrition';
import { CloneScenarioRequest } from '../types/clone-scenario-request.type';
import { CreateScenarioRequest } from '../types/create-scenario-request.type';
import { Scenario } from '../types/scenario.type';

/**
 * TODO: Placeholder for create scenario, similar to Create Calibration
 * A hook for creating a scenario. It attempts to obfuscate boilerplate code out of a component and only expose what is necessary.
 */
export const useCreateScenario = () => {
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const history = useHistory();
  const params = useParams<BluViuNutritionParams>();

  const createScenario = usePost<Scenario, { globalCompanyId: string; request: CreateScenarioRequest }>(
    (request) => postScenario(request.globalCompanyId, request.request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getScenarios', params.globalSiteId]
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const copyScenario = usePost<Scenario, { globalCompanyId: string; scenarioId: string; request: CloneScenarioRequest }>(
    (request) => cloneScenario(request.globalCompanyId, request.scenarioId, request.request),
    (response) => {
      queryClient.invalidateQueries({
        queryKey: ['getScenarios']
      });
      // forward user to the url
      history.push(``);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const deleteScenario = usePost<AxiosResponse, { globalCompanyId: string; scenarioId: string }>(
    (request) => removeScenario(request.globalCompanyId, request.scenarioId),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  return { createScenario, cloneScenario: copyScenario, deleteScenario };
};
